import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { setAccountToken as setDalAccountToken, verifyPassword, updateEmailOrganizationName } from '@littlebooker/dal';
import { saveAuthData, loadAuthData, clearAuthData } from './auth/sessionStorage';
import { setAccessToken, refreshAccessToken, ensureFreshAccessToken, accessTokenExpiresAtMs, onRefresh } from './auth/accessToken';
import type { UserRole, Account, User, AuthData, AuthState, LoginParams } from './auth/authTypes';

// Re-export types for convenience
export type { UserRole, Account, User, AuthData };

type AuthContextType = AuthState & {
  login: (params: LoginParams) => Promise<{ error: string | null }>;
  logout: () => void;
  getToken: () => string | null;
  canCreateUsers: () => boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AuthState>({
    token: null,
    userId: null,
    account: null,
    user: null,
    isLoading: true,
    error: null,
    isAuthenticated: false,
  });

  const signedOutState: AuthState = {
    token: null, userId: null, account: null, user: null,
    isLoading: false, error: null, isAuthenticated: false,
  };

  const resetToSignedOut = useCallback(() => {
    clearAuthData();
    setAccessToken(null);
    setDalAccountToken(null, null);
    queryClient.clear();
    setState(signedOutState);
  }, [queryClient]);

  // Every refresh, from any caller (boot, scheduler, an API call that hit a 401),
  // lands here: keep the DAL's Supabase token and the display data current, or
  // drop to the login screen when the server has ended the session.
  useEffect(() => {
    return onRefresh((outcome) => {
      if (outcome.status === 'signed_out') {
        resetToSignedOut();
        return;
      }
      if (outcome.status !== 'ok') return;
      const { token, supabaseToken, session } = outcome.data;
      setDalAccountToken(supabaseToken, session.account.id);
      saveAuthData({ userId: session.user.id, account: session.account, user: session.user });
      setState({
        token,
        userId: session.user.id,
        account: session.account,
        user: session.user,
        isLoading: false,
        error: null,
        isAuthenticated: true,
      });
    });
  }, [resetToSignedOut]);

  // Restore the session on page load by trading the refresh cookie for an
  // access token. The access token isn't persisted, so this always runs.
  useEffect(() => {
    const restore = async () => {
      const authData = loadAuthData();
      if (!authData) {
        setState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      // Until the refresh returns, the DAL has no Supabase token (→ anon).
      setDalAccountToken(null, authData.account?.id ?? null);

      const outcome = await Promise.race([
        refreshAccessToken(),
        new Promise<{ status: 'timeout' }>(resolve => setTimeout(() => resolve({ status: 'timeout' }), 5000)),
      ]);

      // 'ok' and 'signed_out' are applied by the onRefresh listener above.
      if (outcome.status === 'network_error' || outcome.status === 'timeout') {
        // API unreachable: show the cached session; requests refresh on demand.
        setState({
          token: null,
          userId: authData.userId,
          account: authData.account || null,
          user: authData.user || null,
          isLoading: false,
          error: null,
          isAuthenticated: true,
        });
      }
    };

    restore();
  }, []);

  // Refresh shortly before the access token expires, and straight away when a
  // backgrounded tab comes back (timers don't fire reliably while it sleeps).
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const msUntilRefresh = state.token
      ? Math.max(accessTokenExpiresAtMs() - Date.now() - 60_000, 5_000)
      : 30_000; // no token yet (API was unreachable): retry soon
    const timer = setTimeout(() => { refreshAccessToken(); }, msUntilRefresh);

    const onVisible = () => {
      if (document.visibilityState === 'visible') ensureFreshAccessToken();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [state.isAuthenticated, state.token]);

  const login = async (params: LoginParams): Promise<{ error: string | null }> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    // Modern JWT login (email + password only, no account token needed)
    if ('email' in params && 'password' in params) {
      const { email, password } = params;

      if (!email.trim()) {
        setState(prev => ({ ...prev, isLoading: false, error: 'Email is required' }));
        return { error: 'Email is required' };
      }

      if (!password) {
        setState(prev => ({ ...prev, isLoading: false, error: 'Password is required' }));
        return { error: 'Password is required' };
      }

      try {
        // Call JWT login endpoint
        // Use relative URL if VITE_API_URL is not set (for Vercel serverless functions on same domain)
        const apiUrl = import.meta.env.VITE_API_URL || '';
        const loginUrl = apiUrl ? `${apiUrl}/api/v1/auth/login` : '/api/v1/auth/login';
        const response = await fetch(loginUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include', // Send and receive cookies
          body: JSON.stringify({ email, password }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Login failed' }));
          setState(prev => ({
            ...prev,
            isLoading: false,
            error: errorData.message || 'Invalid email or password',
          }));
          return { error: errorData.message || 'Invalid email or password' };
        }

        const { token: jwtToken, supabaseToken, user: userData, account: accountData } = await response.json();

        const account: Account = {
          id: accountData.id,
          slug: accountData.slug,
          name: accountData.name,
          type: accountData.type,
          is_active: accountData.is_active,
        };

        const user: User = {
          id: userData.id,
          email: userData.email,
          name: userData.name,
          role: userData.role as UserRole,
        };

        // Persist display data only; the access token stays in memory and the
        // session itself is the httpOnly refresh cookie set by this response.
        const authData: AuthData = {
          userId: user.id,
          account,
          user,
        };
        saveAuthData(authData);
        setAccessToken(jwtToken);

        // Forward the Supabase-valid JWT (NOT the app JWT) to the DAL so RLS can
        // scope direct-to-Supabase calls; null → DAL falls back to the anon key.
        setDalAccountToken(supabaseToken ?? null, account.id);

        // Defensive: drop any cached queries from a prior session before
        // app re-renders with the new user. Prevents stale-account data
        // leaking if logout was skipped (e.g., expired session re-login).
        queryClient.clear();

        setState({
          token: jwtToken,
          userId: user.id,
          account,
          user,
          isLoading: false,
          error: null,
          isAuthenticated: true,
        });

        return { error: null };
      } catch (err) {
        console.error('Login error:', err);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Login failed. Please try again.',
        }));
        return { error: 'Login failed. Please try again.' };
      }
    }

    // If we get here, invalid login params
    setState(prev => ({ ...prev, isLoading: false, error: 'Invalid login parameters' }));
    return { error: 'Invalid login parameters' };
  };

  const logout = async () => {
    try {
      // Revokes the session server-side and clears the refresh cookie.
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const logoutUrl = apiUrl ? `${apiUrl}/api/v1/auth/logout` : '/api/v1/auth/logout';
      await fetch(logoutUrl, {
        method: 'POST',
        credentials: 'include',
      }).catch(err => {
        console.warn('Logout endpoint call failed:', err);
        // Continue with client-side cleanup even if server call fails
      });
    } catch (err) {
      console.warn('Logout error:', err);
    }

    // Also drops all React Query caches so the next user can't see the previous
    // session's account-scoped data.
    resetToSignedOut();
  };

  const getToken = () => state.token;

  const canCreateUsers = () => {
    // Only owners can create users
    return state.user?.role === 'owner';
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        getToken,
        canCreateUsers,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Hook to get the current token for API requests
export function useAccountToken(): string | null {
  const { token } = useAuth();
  return token;
}
